import {isAnyOf} from '@reduxjs/toolkit'

import {isEmpty} from '@shared/helpers/lang'

import {getDeliveryAddressFee} from '@web/actions/deliveryInfo'
import {getSelectProductDiscount, updateDeliveryCoupons} from '@web/actions/productCoupon'
import {fetchExpectedRewardPoint} from '@web/actions/rewardPoint'
import {
    OrderFailureType,
    ReasonType,
    DeliveryFailAlertReasons,
    CouponFailReasons,
    ReloadNotAllowedReason,
    ReasonTypeType,
    ErrorType,
} from '@web/core/constants'
import {ErrorState} from '@web/slices/error/slice'
import makeSlice from '@web/slices/error/sliceMaker'

import {initOrderSheetByAPI} from '../../actions'

const isRejectedValidationFailed = isAnyOf(
    getDeliveryAddressFee.rejected,
    fetchExpectedRewardPoint.rejected,
    getSelectProductDiscount.rejected,
    updateDeliveryCoupons.rejected,
)

const isFailureCase = (reasonType: string) => {
    const FailureCaseBlackList: string[] = [
        ReasonType.ORDER_ALREADY_CREATED,
        ...DeliveryFailAlertReasons,
        ...CouponFailReasons,
    ]
    return !FailureCaseBlackList.includes(reasonType)
}

const errorSlice = makeSlice({
    extraReducers(builder) {
        builder
            .addCase(fetchExpectedRewardPoint.rejected, (state, {payload}) => {
                if (payload?.code === ReasonType.GET_REWARD_POLICY_FAILED) {
                    state.exceptionCodeMap = {
                        GET_REWARD_POLICY_FAILED: {
                            type: OrderFailureType.GET_REWARD_POLICY_FAILED,
                            code: ReasonType.GET_REWARD_POLICY_FAILED,
                            message: payload?.validationFailure?.fallbackMessage,
                        },
                    }
                } else if (payload?.validationFailure?.reasonType === ReasonType.INVALID_SELECTED_PRODUCT_COUPON) {
                    state.exceptionCodeMap = {
                        INVALID_SELECTED_PRODUCT_COUPON: {
                            type: ReasonType.INVALID_SELECTED_PRODUCT_COUPON,
                            code: ReasonType.INVALID_SELECTED_PRODUCT_COUPON,
                            message: payload.validationFailure.fallbackMessage,
                        },
                    }
                }
            })
            .addCase(initOrderSheetByAPI.rejected, (state, {payload}) => {
                state.failureCase = (function (): ErrorState['failureCase'] {
                    const code = payload?.code ?? ''

                    const fallbackMessage = payload?.validationFailure?.fallbackMessage ?? ''
                    // 페이머니 회원조회 실패
                    if (code === ReasonType.GET_CHARGE_POINT_USER_FAILED) {
                        return {
                            type: OrderFailureType.UNKNOWN,
                            code: ReasonType.GET_CHARGE_POINT_USER_FAILED,
                            message: fallbackMessage,
                            attributes: {
                                userAuthStatus: payload?.userAuthStatus,
                            },
                        }
                    }
                    if (code === ReasonType.GET_REWARD_POLICY_FAILED) {
                        return {
                            type: OrderFailureType.GET_REWARD_POLICY_FAILED,
                            code: ReasonType.GET_REWARD_POLICY_FAILED,
                            message: fallbackMessage,
                            attributes: {
                                userAuthStatus: payload?.userAuthStatus,
                            },
                        }
                    }
                    if (code === 'VALIDATION_FAILED') {
                        const reasonType = payload?.validationFailure?.reasonType
                        if (reasonType === ReasonType.BLACK_CONSUMER || reasonType === ReasonType.BLACKCONSUMER) {
                            const denyingMerchants = payload?.denyingMerchants
                            return {
                                type: OrderFailureType.REFUSED_PURCHASER,
                                code: ReasonType.BLACK_CONSUMER,
                                message: fallbackMessage,
                                ...(function () {
                                    if (denyingMerchants && !isEmpty(denyingMerchants)) {
                                        return {
                                            attributes: {
                                                merchants: denyingMerchants.map(
                                                    ({merchantName, customerCenterPhoneNumber}) => {
                                                        return {
                                                            name: merchantName,
                                                            tel: customerCenterPhoneNumber,
                                                        }
                                                    },
                                                ),
                                            },
                                        }
                                    }
                                    return {}
                                })(),
                                attributes: {
                                    userAuthStatus: payload?.userAuthStatus,
                                },
                            }
                        }
                        if (reasonType === ReasonType.ORDER_ALREADY_CREATED) {
                            return {
                                type: OrderFailureType.ORDER_ALREADY_CREATED,
                                code: ReasonType.ORDER_ALREADY_CREATED,
                                attributes: {
                                    userAuthStatus: payload?.userAuthStatus,
                                },
                            }
                        }
                        if (reasonType === ReasonType.CAFE_MERCHANT_IDENTICAL_TO_CUSTOMER) {
                            return {
                                type: OrderFailureType.BUY_SELF_OWNED,
                                code: ReasonType.CAFE_MERCHANT_IDENTICAL_TO_CUSTOMER,
                                attributes: {
                                    userAuthStatus: payload?.userAuthStatus,
                                },
                            }
                        }
                        if (ReloadNotAllowedReason.includes(reasonType as ReasonTypeType)) {
                            return {
                                type: OrderFailureType.BY_REDIRECT,
                                code,
                                message: '',
                                attributes: {
                                    userAuthStatus: payload?.userAuthStatus,
                                },
                            }
                        }
                    }
                    if (code === ErrorType.ETIMEDOUT_FAILED) {
                        return {
                            type: OrderFailureType.BY_REDIRECT,
                            code,
                            message: '',
                            attributes: {
                                userAuthStatus: payload?.userAuthStatus,
                            },
                        }
                    }
                    if (code === ReasonType.ORDER_SHEET_NOT_FOUND) {
                        return {
                            type: OrderFailureType.UNKNOWN,
                            code: ReasonType.ORDER_SHEET_NOT_FOUND,
                            message: payload?.fallbackMessage,
                            attributes: {
                                userAuthStatus: payload?.userAuthStatus,
                            },
                        }
                    }
                    if (payload && payload.code && payload.fallbackMessage) {
                        return {
                            type: OrderFailureType.UNKNOWN,
                            code: payload.code,
                            message: payload.fallbackMessage,
                            attributes: {
                                userAuthStatus: payload?.userAuthStatus,
                            },
                        }
                    }
                    return {
                        type: OrderFailureType.UNKNOWN,
                        code: ReasonType.INQUIRE_ORDER_SHEET_FAILED,
                        attributes: {
                            userAuthStatus: payload?.userAuthStatus,
                        },
                    }
                })()
            })
            .addMatcher(isRejectedValidationFailed, (state, {payload}) => {
                if (payload) {
                    if (payload.code === 'VALIDATION_FAILED') {
                        const reasonType = payload?.validationFailure.reasonType
                        if (isFailureCase(reasonType)) {
                            state.failureCase = {
                                type: OrderFailureType.UNKNOWN,
                                code: reasonType ?? ReasonType.INQUIRE_ORDER_SHEET_FAILED,
                                message: payload?.validationFailure.fallbackMessage,
                            }
                        }
                    } else if (payload.code === 'ORDER_SHEET_NOT_FOUND') {
                        state.failureCase = {
                            type: OrderFailureType.UNKNOWN,
                            code: ReasonType.INQUIRE_ORDER_SHEET_FAILED,
                        }
                    }
                }
            })
    },
})

export default errorSlice.reducer
export const actions = errorSlice.actions
